import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Professional preset suggestions
const SALUTATION_PRESETS = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];

const SIGNATORY_PRESETS = [
  'Principal',
  'Director',
  'Managing Trustee',
  'Academic Coordinator',
  'Vice Principal',
  'Administrator'
];

const DESIGNATION_PRESETS = [
  'Primary Teacher (PRT)',
  'Trained Graduate Teacher (TGT)',
  'Post Graduate Teacher (PGT)',
  'Class Teacher',
  'Subject Teacher',
  'Assistant Teacher',
  'Senior Faculty',
  'Sports Teacher / PET',
  'Art & Craft Teacher',
  'Music & Dance Teacher',
  'Computer & IT Teacher',
  'Tuition / Remedial Teacher'
];

const DEPARTMENT_PRESETS = [
  'Primary Wing',
  'Middle School',
  'Secondary School',
  'Senior Secondary',
  'Mathematics',
  'Science',
  'Social Studies',
  'English & Languages',
  'Hindi',
  'Computer Science',
  'Physical Education',
  'Arts & Humanities'
];

const CONDUCT_PRESETS = [
  { label: 'Exemplary', text: 'exemplary professionalism, profound subject expertise, and commendable devotion to student welfare' },
  { label: 'Dedicated & Punctual', text: 'remarkable dedication, punctuality, strong work ethics, and collaborative teamwork throughout the tenure' },
  { label: 'Innovative & Inspiring', text: 'innovative teaching pedagogy, great classroom leadership, and active participation in school co-curricular initiatives' },
  { label: 'Sincere & Reliable', text: 'sincerity, high sense of responsibility, and consistent contribution toward maintaining academic excellence' },
];

const REMARKS_PRESETS = [
  { label: 'Clearance & Good Standing', text: 'All institutional clearances, academic registers, and school properties have been properly accounted for. The candidate relieved in good standing.' },
  { label: 'Probation / General Terms', text: 'This position involves a 6-month probation period during which performance and institutional alignment will be evaluated.' },
  { label: 'High Regard Recommendation', text: 'Highly recommended for future academic and leadership roles without reservation.' }
];

const fmtDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

const calcDuration = (from, to) => {
  if (!from || !to) return '';
  const s = new Date(from), e = new Date(to);
  let y = e.getFullYear() - s.getFullYear();
  let m = e.getMonth() - s.getMonth();
  let d = e.getDate() - s.getDate();
  if (d < 0) { m--; d += new Date(e.getFullYear(), e.getMonth(), 0).getDate(); }
  if (m < 0) { y--; m += 12; }
  const parts = [];
  if (y > 0) parts.push(`${y} Year${y > 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m} Month${m > 1 ? 's' : ''}`);
  if (d > 0) parts.push(`${d} Day${d > 1 ? 's' : ''}`);
  return parts.join(', ') || 'Less than a day';
};

const todayStr = () => new Date().toISOString().split('T')[0];

const getHonorificFullName = (data) => {
  const sal = (data?.salutation || '').trim();
  const name = (data?.teacherName || '').trim();
  if (!sal) return name;
  if (name.toLowerCase().startsWith(sal.toLowerCase())) return name;
  return `${sal} ${name}`;
};

// ── STABLE OUTSIDE FORM HELPERS (PREVENT FOCUS LOSS) ─────────────────────
const FormField = ({ label, value, onChange, type = 'text', required, placeholder, listId, helper, disabled }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: '#1e3a8a' }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {helper && <span style={{ fontSize: 11, color: '#64748b' }}>{helper}</span>}
    </div>
    <input
      type={type}
      list={listId}
      placeholder={placeholder}
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled}
      style={{
        width: '100%', padding: '9px 12px', borderRadius: 8,
        border: '1.5px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box',
        backgroundColor: disabled ? '#f8fafc' : '#ffffff',
        color: disabled ? '#64748b' : '#0f172a',
        outline: 'none',
        fontFamily: "Arial, 'Helvetica Neue', sans-serif"
      }}
      onFocus={e => { if (!disabled) e.target.style.borderColor = '#1e3a8a'; }}
      onBlur={e => { if (!disabled) e.target.style.borderColor = '#cbd5e1'; }}
    />
  </div>
);

const FormTextarea = ({ label, value, onChange, placeholder, presets = [], onSelectPreset, onClear }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: '#1e3a8a' }}>{label}</label>
      {value ? (
        <button
          type="button"
          onClick={onClear}
          style={{
            border: 'none', background: 'transparent', color: '#dc2626',
            fontSize: 11, cursor: 'pointer', fontWeight: 700, padding: 0
          }}
        >
          [Clear text]
        </button>
      ) : null}
    </div>

    {presets && presets.length > 0 && (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Quick presets:</span>
        {presets.map((p, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onSelectPreset(p.text)}
            style={{
              background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 14,
              padding: '3px 9px', fontSize: 11, color: '#1e3a8a', cursor: 'pointer',
              fontWeight: 600, transition: 'all 0.15s', whiteSpace: 'nowrap'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; }}
          >
            + {p.label}
          </button>
        ))}
      </div>
    )}

    <textarea
      placeholder={placeholder}
      value={value ?? ''}
      onChange={onChange}
      rows={3}
      style={{
        width: '100%', padding: '9px 12px', borderRadius: 8,
        border: '1.5px solid #cbd5e1', fontSize: 13, resize: 'vertical',
        boxSizing: 'border-box', backgroundColor: '#ffffff', color: '#0f172a',
        fontFamily: "Arial, 'Helvetica Neue', sans-serif", lineHeight: 1.6, outline: 'none'
      }}
      onFocus={e => e.target.style.borderColor = '#1e3a8a'}
      onBlur={e => e.target.style.borderColor = '#cbd5e1'}
    />
  </div>
);

const FormCheckbox = ({ label, checked, onChange }) => (
  <label style={{
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5,
    color: '#1e293b', fontWeight: 600, cursor: 'pointer', margin: '4px 0'
  }}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={e => onChange(e.target.checked)}
      style={{ accentColor: '#1e3a8a', width: 16, height: 16, cursor: 'pointer' }}
    />
    <span>{label}</span>
  </label>
);

const Letterhead = ({ data = {} }) => {
  const showPhone = data.showPhone !== false;
  const showWebsite = data.showWebsite !== false;
  const showEmail = !!data.showEmail && !!data.contactEmail;
  const showTrust = data.showTrust !== false;

  return (
    <div style={{ borderBottom: '3px double #1e3a8a', paddingBottom: 16, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <div style={{
          width: 80, height: 80, background: '#1e3a8a', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', flexShrink: 0, border: '3px solid #1e3a8a',
          boxShadow: '0 2px 8px rgba(30, 58, 138, 0.2)'
        }}>
          <img src="/logo.png" alt="logo"
            style={{ width: 72, height: 72, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerText = 'TRS'; }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 25, fontWeight: 900, color: '#1e3a8a', letterSpacing: 1, fontFamily: "Arial, 'Helvetica Neue', sans-serif" }}>
            THE RISING SCHOOL
          </div>
          <div style={{ fontSize: 12, color: '#334155', fontWeight: 600, letterSpacing: 0.5, marginTop: 2, fontFamily: "Arial, sans-serif" }}>
            Where Knowledge Meets The Dawn
          </div>
          <div style={{ fontSize: 11, color: '#475569', marginTop: 4, fontFamily: "Arial, sans-serif", lineHeight: 1.5 }}>
            {data.campusAddress || 'Sector 12 Savina, Udaipur, Rajasthan'}
            {showPhone && <> &nbsp;|&nbsp; Phone: {data.contactPhone || '+91 8209683549'}</>}
            {showWebsite && <> &nbsp;|&nbsp; Web: {data.contactWebsite || 'www.therisingschool.com'}</>}
            {showEmail && <> &nbsp;|&nbsp; Email: {data.contactEmail}</>}
          </div>
          {showTrust && (
            <div style={{ fontSize: 11, color: '#1e3a8a', fontWeight: 700, marginTop: 2, fontFamily: "Arial, sans-serif" }}>
              The Synergy Foundation
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LetterFooter = ({ data = {} }) => {
  const showFootnote = data.showVerificationFootnote !== false;
  const signatory = data.issuedBy || 'Principal';
  const showPhone = data.showPhone !== false;

  return (
    <div style={{ marginTop: 52 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div style={{ fontSize: 10.5, color: '#64748b', lineHeight: 1.6, fontFamily: "Arial, sans-serif" }}>
          {showPhone && <div><b>Official Verification:</b> {data.contactPhone || '+91 8209683549'}</div>}
          <div>School Campus: {data.campusAddress || 'Sector 12 Savina, Udaipur, Rajasthan'}</div>
          {showFootnote && (
            <div style={{ fontSize: 9.5, color: '#94a3b8' }}>System-generated authentic institutional certificate</div>
          )}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ height: 48, borderBottom: '1.5px solid #1e3a8a', width: 220, marginBottom: 6 }} />
          <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1e3a8a', fontFamily: "Arial, sans-serif" }}>{signatory}</div>
          <div style={{ fontSize: 11.5, color: '#334155', fontWeight: 600, fontFamily: "Arial, sans-serif" }}>The Rising School, Udaipur</div>
          <div style={{ fontSize: 10, color: '#64748b', fontFamily: "Arial, sans-serif" }}>Office of the Principal / Management</div>
        </div>
      </div>
      <div style={{
        marginTop: 22, borderTop: '2px solid #1e3a8a', paddingTop: 8,
        textAlign: 'center', fontSize: 10, color: '#1e3a8a', fontWeight: 600, letterSpacing: 0.5,
        fontFamily: "Arial, sans-serif"
      }}>
        THE RISING SCHOOL &nbsp;|&nbsp; SECTOR 12 SAVINA, UDAIPUR (RAJASTHAN) &nbsp;|&nbsp; WWW.THERISINGSCHOOL.COM
      </div>
    </div>
);
};

const JoiningLetterDoc = React.forwardRef(({ data }, ref) => {
  const fullName = getHonorificFullName(data);
  return (
    <div ref={ref} style={{
      width: 794, minHeight: 1123, background: '#ffffff',
      padding: '48px 64px', boxSizing: 'border-box',
      fontFamily: "Arial, 'Helvetica Neue', Helvetica, sans-serif", fontSize: 13, color: '#0f172a', lineHeight: 1.85,
      border: '1px solid #cbd5e1', margin: '0 auto'
    }}>
      <Letterhead data={data} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, fontSize: 12, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
        <div><b>Ref. No:</b> <span style={{ fontWeight: 'bold', color: '#1e3a8a' }}>{data.refNumber}</span></div>
        <div><b>Date of Issue:</b> {fmtDate(data.issueDate)}</div>
      </div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <span style={{
          display: 'inline-block', background: '#1e3a8a', color: '#ffffff',
          padding: '6px 32px', borderRadius: 4, fontSize: 14, fontWeight: 'bold', letterSpacing: 1.5,
        }}>OFFICIAL APPOINTMENT LETTER</span>
      </div>
      <div style={{ marginBottom: 18, lineHeight: 1.6 }}>
        <b>To,</b><br />
        <span style={{ fontSize: 14, fontWeight: 'bold', color: '#1e3a8a' }}>{fullName}</span><br />
        {data.designation && <>{data.designation}<br /></>}
        {data.department && <>{data.department}<br /></>}
        {data.employeeId && <><b>Employee ID:</b> {data.employeeId}<br /></>}
      </div>
      <p>Dear <b>{fullName}</b>,</p>
      <p style={{ textAlign: 'justify' }}>
        With reference to your application and subsequent interview, the Management of
        <b> The Rising School, Savina, Udaipur</b> is pleased to appoint you as
        <b> {data.designation || 'Teacher'}</b>
        {data.department ? ` in the ${data.department}` : ''}. Your appointment is effective from
        <b> {fmtDate(data.joiningDate)}</b>.
      </p>
      <p style={{ textAlign: 'justify' }}>
        You are allocated Employee ID: <b>{data.employeeId || 'To be assigned'}</b>.
        You are requested to report to the Principal's Office on your joining date with your primary academic certificates,
        prior service certificates, photo identification, and photographs for completion of formal joining procedures.
      </p>
      <p style={{ textAlign: 'justify' }}>
        This appointment is governed by the institutional code of conduct and rules in force at The Rising School.
        You are expected to maintain high standards of teaching, professional integrity, punctuality, and student mentorship.
      </p>
      {data.remarks && (
        <div style={{
          background: '#f8fafc', borderLeft: '4px solid #1e3a8a',
          padding: '12px 16px', margin: '16px 0', fontSize: 12.5, lineHeight: 1.6,
        }}>
          <b>Specific Terms and Remarks:</b><br />
          {data.remarks}
        </div>
      )}
      <p style={{ textAlign: 'justify' }}>
        We welcome you to The Rising School team and look forward to your dedicated contribution to the educational development of our students.
      </p>
      <p style={{ marginTop: 20 }}>Yours sincerely,</p>
      <LetterFooter data={data} />
    </div>
);
});

const ExperienceLetterDoc = React.forwardRef(({ data }, ref) => {
  const duration = calcDuration(data.joiningDate, data.leavingDate);
  const fullName = getHonorificFullName(data);
  const showTenure = data.showServiceTenure !== false;

  return (
    <div ref={ref} style={{
      width: 794, minHeight: 1123, background: '#ffffff',
      padding: '48px 64px', boxSizing: 'border-box',
      fontFamily: "Arial, 'Helvetica Neue', Helvetica, sans-serif", fontSize: 13, color: '#0f172a', lineHeight: 1.85,
      border: '1px solid #cbd5e1', margin: '0 auto'
    }}>
      <Letterhead data={data} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, fontSize: 12, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
        <div><b>Ref. No:</b> <span style={{ fontWeight: 'bold', color: '#1e3a8a' }}>{data.refNumber}</span></div>
        <div><b>Date of Issue:</b> {fmtDate(data.issueDate)}</div>
      </div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <span style={{
          display: 'inline-block', background: '#1e3a8a', color: '#ffffff',
          padding: '6px 32px', borderRadius: 4, fontSize: 14, fontWeight: 'bold', letterSpacing: 1.5,
        }}>EXPERIENCE AND SERVICE CERTIFICATE</span>
      </div>
      <p style={{ fontSize: 13.5 }}><b>TO WHOMSOEVER IT MAY CONCERN</b></p>
      <p style={{ textAlign: 'justify' }}>
        This is to certify that <b>{fullName}</b>
        {data.employeeId ? ` (Employee ID: ${data.employeeId})` : ''} was employed as an academic faculty member at
        <b> The Rising School, Savina, Udaipur (Rajasthan)</b>.
        {' '}{fullName} served with distinction in the capacity of <b>{data.designation || 'Teacher'}</b>
        {data.department ? ` in the ${data.department}` : ''}.
      </p>
      <div style={{
        background: '#f8fafc', border: '1.5px solid #cbd5e1',
        borderRadius: 6, padding: '14px 20px', margin: '18px 0', fontSize: 12.5,
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
          <div><b>Date of Joining:</b> {fmtDate(data.joiningDate)}</div>
          <div><b>Date of Relieving:</b> {fmtDate(data.leavingDate)}</div>
          {showTenure && duration && (
            <div style={{ gridColumn: '1/-1', borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4 }}>
              <b>Total Tenure of Service:</b> <span style={{ color: '#1e3a8a', fontWeight: 'bold' }}>{duration}</span>
            </div>
          )}
        </div>
      </div>
      <p style={{ textAlign: 'justify' }}>
        During the tenure of employment at our institution, <b>{fullName}</b> demonstrated
        {data.conductStatement
          ? ` ${data.conductStatement}`
          : ' exemplary professionalism, profound subject expertise, and commendable devotion to student welfare'}.
        {' '}{fullName} maintained a cordial and constructive working relationship with colleagues, students, and administration.
      </p>
      {data.remarks && (
        <div style={{
          background: '#f8fafc', borderLeft: '4px solid #1e3a8a',
          padding: '12px 16px', margin: '16px 0', fontSize: 12.5, lineHeight: 1.6,
        }}>
          <b>Institutional Observations and Remarks:</b><br />
          {data.remarks}
        </div>
      )}
      <p style={{ textAlign: 'justify' }}>
        We place on record our sincere appreciation for the dedicated service rendered during this period and convey our best wishes for all future career pursuits.
      </p>
      <p style={{ marginTop: 22 }}>Yours sincerely,</p>
      <LetterFooter data={data} />
    </div>
  );
});

export { JoiningLetterDoc, ExperienceLetterDoc, calcDuration, fmtDate, todayStr };

function TeacherDocuments({ user, onClose }) {
  const [view, setView]             = useState('list');
  const [teachers, setTeachers]     = useState([]);
  const [search, setSearch]         = useState('');
  const [selTeacher, setSelTeacher] = useState(null);
  const [docType, setDocType]       = useState('experience');
  const [history, setHistory]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg]               = useState('');
  const [activeTab, setActiveTab]   = useState('core');
  const printRef = useRef(null);

  const [form, setForm] = useState({
    refNumber: '',
    issueDate: todayStr(),
    salutation: 'Mrs.',
    teacherName: '',
    designation: 'Trained Graduate Teacher (TGT)',
    department: 'Secondary School - Mathematics & Science',
    employeeId: '',
    joiningDate: '',
    leavingDate: '',
    showServiceTenure: true,
    conductStatement: '',
    remarks: '',
    issuedBy: user?.name || 'Principal',
    campusAddress: 'Sector 12 Savina, Udaipur, Rajasthan',
    contactPhone: '+91 8209683549',
    contactWebsite: 'www.therisingschool.com',
    contactEmail: 'info@therisingschool.com',
    showPhone: true,
    showWebsite: true,
    showEmail: false,
    showTrust: true,
    showVerificationFootnote: true,
  });

  useEffect(() => { fetchTeachers(); }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/users`);
      setTeachers(res.data.filter(u => u.role === 'teacher' || u.role === 'tuition_teacher'));
    } catch { setMsg('Failed to load teachers'); }
    finally { setLoading(false); }
  };

  const getCleanFormalName = (rawName = '') => {
    let n = rawName.replace(/\b(sir|madam|ma'am|maam|ji)\b/gi, '').trim();
    n = n.replace(/\s+/g, ' ');
    return n;
  };

  const openForm = async (teacher, type) => {
    setSelTeacher(teacher); setDocType(type); setMsg('');
    let ref = `TRS/${type === 'joining' ? 'JL' : 'EL'}/${new Date().getFullYear()}/001`;
    try {
      const r = await axios.get(`${API_BASE_URL}/api/teacher-documents/next-ref/${type}`);
      ref = r.data.ref;
    } catch {}

    const cleanName = getCleanFormalName(teacher.name || '');
    const cleanDept = teacher.subject || teacher.class
      ? `${teacher.subject || `Class ${teacher.class}`} Department`
      : 'Primary Wing';

    setForm({
      refNumber: ref,
      issueDate: todayStr(),
      salutation: 'Mrs.',
      teacherName: cleanName,
      designation: teacher.designation || (teacher.class ? `Class ${teacher.class} Teacher` : 'Teacher'),
      department: cleanDept,
      employeeId: teacher.employeeId || teacher.studentId || '',
      joiningDate: teacher.joiningDate ? teacher.joiningDate.split('T')[0] : '',
      leavingDate: todayStr(),
      showServiceTenure: true,
      conductStatement: '',
      remarks: '',
      issuedBy: user?.name || 'Principal',
      campusAddress: 'Sector 12 Savina, Udaipur, Rajasthan',
      contactPhone: '+91 8209683549',
      contactWebsite: 'www.therisingschool.com',
      contactEmail: 'info@therisingschool.com',
      showPhone: true,
      showWebsite: true,
      showEmail: false,
      showTrust: true,
      showVerificationFootnote: true,
    });
    setView('editor');
  };

  const openHistory = async (teacher) => {
    setSelTeacher(teacher); setMsg('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/teacher-documents/${teacher._id}`);
      setHistory(res.data);
    } catch { setHistory([]); }
    setView('history');
  };

  const handleResetToDefault = () => {
    if (!selTeacher) return;
    const cleanName = getCleanFormalName(selTeacher.name || '');
    setForm(f => ({
      ...f,
      salutation: 'Mrs.',
      teacherName: cleanName,
      designation: selTeacher.designation || (selTeacher.class ? `Class ${selTeacher.class} Teacher` : 'Teacher'),
      department: selTeacher.subject || selTeacher.class ? `${selTeacher.subject || `Class ${selTeacher.class}`} Department` : 'Primary Wing',
      employeeId: selTeacher.employeeId || selTeacher.studentId || '',
      joiningDate: selTeacher.joiningDate ? selTeacher.joiningDate.split('T')[0] : '',
      leavingDate: todayStr(),
      conductStatement: '',
      remarks: '',
      issuedBy: user?.name || 'Principal',
      showServiceTenure: true,
      showPhone: true,
      showWebsite: true,
      showEmail: false,
      showTrust: true,
      showVerificationFootnote: true,
    }));
    setMsg('Form values reset to default.');
    setTimeout(() => setMsg(''), 2500);
  };

  const handleSaveChanges = async () => {
    setSaving(true); setMsg('');
    try {
      const payload = {
        teacherId: selTeacher._id,
        teacherName: getHonorificFullName(form),
        docType,
        ...form,
        letterData: { ...form, docType },
      };
      await axios.post(`${API_BASE_URL}/api/teacher-documents`, payload);
      setMsg('Document recorded successfully in official archives.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF }   = await import('jspdf');
      const el = printRef.current;
      if (!el) return;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#fff', logging: false });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, 210, 297);
      const safeRef = (form.refNumber || 'certificate').replace(/\//g, '_');
      const safeName = (getHonorificFullName(form) || 'Teacher').replace(/\s+/g, '_');
      pdf.save(`${safeRef}_${safeName}.pdf`);
    } catch (err) { setMsg('PDF generation failed: ' + err.message); }
    finally { setDownloading(false); }
  };

  const handleDirectPrint = () => {
    const el = printRef.current;
    if (!el) { window.print(); return; }
    const printWin = window.open('', '_blank', 'width=900,height=1000');
    if (!printWin) { window.print(); return; }
    printWin.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${form.refNumber} - Print</title>
          <style>
            @page { size: A4 portrait; margin: 0; }
            body { margin: 0; padding: 0; background: #fff; font-family: Arial, sans-serif; }
          </style>
        </head>
        <body>${el.outerHTML}</body>
      </html>
    `);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => {
      printWin.print();
      printWin.close();
    }, 450);
  };

  const handleRevise = async (doc) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/teacher-documents/${doc._id}/revise`, {
        issueDate: todayStr(), letterData: doc.letterData,
      });
      setHistory(h => [res.data, ...h]);
      setMsg('Revised: ' + res.data.refNumber);
    } catch (err) { setMsg('Failed: ' + (err.response?.data?.error || err.message)); }
  };

  const filtered = teachers.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.email?.toLowerCase().includes(search.toLowerCase())
  );

  const MsgBar = () => msg ? (
    <div style={{
      padding: '9px 14px', borderRadius: 8,
      background: msg.includes('Failed') ? '#fef2f2' : '#f0fdf4',
      border: `1px solid ${msg.includes('Failed') ? '#fca5a5' : '#86efac'}`,
      color: msg.includes('Failed') ? '#991b1b' : '#166534',
      marginBottom: 12, fontSize: 12.5, fontWeight: 600
    }}>
      {msg}
    </div>
  ) : null;

  const panelStyle = { background: '#fff', borderRadius: 16, padding: 26, width: '100%', maxWidth: 740, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.88)',
      backdropFilter: 'blur(3px)', zIndex: 9999, display: 'flex', flexDirection: 'column',
      alignItems: 'center', overflowY: 'auto', padding: '16px 14px',
    }}>

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <div style={panelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, borderBottom: '1.5px solid #e2e8f0', paddingBottom: 12 }}>
            <div>
              <h2 style={{ margin: 0, color: '#1e3a8a', fontSize: 20, fontWeight: 900 }}>Teacher Documents Portal</h2>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Official Experience Certificates and Appointment Letters</div>
            </div>
            <button onClick={onClose} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 15px', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Close</button>
          </div>
          <input
            type="text"
            placeholder="Search teacher by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid #93c5fd', fontSize: 13, marginBottom: 14, boxSizing: 'border-box', outline: 'none' }}
          />
          <MsgBar />
          {loading && <div style={{ textAlign: 'center', padding: 24, color: '#1e3a8a', fontWeight: 600 }}>Loading teachers...</div>}
          {filtered.map(t => (
            <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 10, marginBottom: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                background: t.profilePicture ? `url(${API_BASE_URL}${t.profilePicture}) center/cover` : '#1e3a8a',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700,
              }}>
                {!t.profilePicture && (t.name?.charAt(0) || 'T')}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>{t.name}</div>
                <div style={{ fontSize: 11.5, color: '#64748b' }}>
                  {t.role === 'tuition_teacher' ? 'Tuition Teacher' : 'Teacher'}
                  {t.class ? ` · Class ${t.class}` : ''} · {t.email}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button onClick={() => openForm(t, 'experience')} style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 13px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  Experience Letter
                </button>
                <button onClick={() => openForm(t, 'joining')} style={{ background: '#f1f5f9', color: '#1e3a8a', border: '1px solid #bfdbfe', borderRadius: 7, padding: '7px 13px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  Appointment Letter
                </button>
                <button onClick={() => openHistory(t)} style={{ background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 7, padding: '7px 11px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  History
                </button>
              </div>
            </div>
          ))}
          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 36, color: '#94a3b8' }}>No faculty members found matching search.</div>
          )}
        </div>
      )}

      {/* ── LIVE SPLIT EDITOR VIEW (FORM CONTROLS + A4 PREVIEW) ── */}
      {view === 'editor' && selTeacher && (
        <div style={{ width: '100%', maxWidth: 1380, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Top Bar */}
          <div style={{
            background: '#ffffff', borderRadius: 12, padding: '12px 20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)', borderLeft: '5px solid #1e3a8a'
          }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 900, color: '#1e3a8a' }}>
                {docType === 'experience' ? 'Experience & Service Certificate Generator' : 'Official Appointment Letter Generator'}
              </div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                Live Editing for <b>{getHonorificFullName(form)}</b> &nbsp;|&nbsp;
                Ref: <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e3a8a' }}>{form.refNumber}</span> (Locked)
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleResetToDefault}
                style={{ background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
              >
                Reset to Default
              </button>
              <button
                type="button"
                onClick={handleSaveChanges}
                disabled={saving}
                style={{ background: saving ? '#94a3b8' : '#1e3a8a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={handleDirectPrint}
                style={{ background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
              >
                Direct Print
              </button>
              <button
                type="button"
                onClick={handleDownloadPDF}
                disabled={downloading}
                style={{ background: downloading ? '#94a3b8' : 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 12.5, fontWeight: 700, cursor: downloading ? 'not-allowed' : 'pointer' }}
              >
                {downloading ? 'Generating...' : 'Download High-Resolution PDF'}
              </button>
              <button
                type="button"
                onClick={() => { setView('list'); setMsg(''); }}
                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
              >
                Back to List
              </button>
            </div>
          </div>

          <MsgBar />

          {/* Main 2-Column Workspace */}
          <div style={{ display: 'grid', gridTemplateColumns: '460px 1fr', gap: 16, alignItems: 'start' }}>

            {/* LEFT COLUMN: EDITABLE CONTROLS SIDEBAR */}
            <div style={{
              background: '#ffffff', borderRadius: 14, padding: '18px 20px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)', maxHeight: 'calc(100vh - 150px)',
              overflowY: 'auto', border: '1.5px solid #bfdbfe'
            }}>
              {/* Category Tabs */}
              <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', padding: 4, borderRadius: 8, marginBottom: 14 }}>
                {[
                  { id: 'core', label: 'Faculty & Role' },
                  { id: 'service', label: 'Dates & Tenure' },
                  { id: 'text', label: 'Conduct & Remarks' },
                  { id: 'letterhead', label: 'Institutional' }
                ].map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    style={{
                      flex: 1, padding: '6px 4px', border: 'none', borderRadius: 6,
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      background: activeTab === t.id ? '#1e3a8a' : 'transparent',
                      color: activeTab === t.id ? '#ffffff' : '#475569',
                      transition: 'all 0.15s'
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <datalist id="salutation-options">
                {SALUTATION_PRESETS.map((s, i) => <option key={i} value={s} />)}
              </datalist>
              <datalist id="designation-options">
                {DESIGNATION_PRESETS.map((d, i) => <option key={i} value={d} />)}
              </datalist>
              <datalist id="department-options">
                {DEPARTMENT_PRESETS.map((d, i) => <option key={i} value={d} />)}
              </datalist>
              <datalist id="signatory-options">
                {SIGNATORY_PRESETS.map((s, i) => <option key={i} value={s} />)}
              </datalist>

              {/* TAB 1: CORE FACULTY & ROLE */}
              {activeTab === 'core' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '0 10px' }}>
                    <FormField
                      label="Salutation"
                      value={form.salutation}
                      onChange={e => setForm(f => ({ ...f, salutation: e.target.value }))}
                      listId="salutation-options"
                      placeholder="e.g. Mrs."
                    />
                    <FormField
                      label="Full Legal Name"
                      value={form.teacherName}
                      onChange={e => setForm(f => ({ ...f, teacherName: e.target.value }))}
                      required
                      placeholder="e.g. Shabbo Khan"
                    />
                  </div>

                  <FormField
                    label="Official Designation"
                    value={form.designation}
                    onChange={e => setForm(f => ({ ...f, designation: e.target.value }))}
                    listId="designation-options"
                    placeholder="e.g. Trained Graduate Teacher (TGT)"
                    required
                  />

                  <FormField
                    label="Department / Grade"
                    value={form.department}
                    onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                    listId="department-options"
                    placeholder="e.g. Middle School - Mathematics & Science"
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
                    <FormField
                      label="Employee Code"
                      value={form.employeeId}
                      onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))}
                      placeholder="e.g. TRS-T-042"
                    />
                    <FormField
                      label="Signatory Role"
                      value={form.issuedBy}
                      onChange={e => setForm(f => ({ ...f, issuedBy: e.target.value }))}
                      listId="signatory-options"
                      placeholder="e.g. Principal"
                    />
                  </div>
                </div>
              )}

              {/* TAB 2: SERVICE DATES & TENURE */}
              {activeTab === 'service' && (
                <div>
                  <FormField
                    label="Reference Number (Locked)"
                    value={form.refNumber}
                    onChange={() => {}}
                    disabled
                    helper="Immutable identifier"
                  />

                  <FormField
                    label="Date of Issue"
                    value={form.issueDate}
                    onChange={e => setForm(f => ({ ...f, issueDate: e.target.value }))}
                    type="date"
                    required
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
                    <FormField
                      label="Date of Joining"
                      value={form.joiningDate}
                      onChange={e => setForm(f => ({ ...f, joiningDate: e.target.value }))}
                      type="date"
                      required
                    />
                    {docType === 'experience' && (
                      <FormField
                        label="Date of Relieving"
                        value={form.leavingDate}
                        onChange={e => setForm(f => ({ ...f, leavingDate: e.target.value }))}
                        type="date"
                        required
                      />
                    )}
                  </div>

                  {docType === 'experience' && form.joiningDate && form.leavingDate && (
                    <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', margin: '8px 0 12px 0' }}>
                      <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Calculated Tenure
                      </div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1e3a8a', marginTop: 2 }}>
                        {calcDuration(form.joiningDate, form.leavingDate)}
                      </div>
                    </div>
                  )}

                  {docType === 'experience' && (
                    <FormCheckbox
                      label="Calculate and display total service tenure automatically"
                      checked={form.showServiceTenure}
                      onChange={val => setForm(f => ({ ...f, showServiceTenure: val }))}
                    />
                  )}
                </div>
              )}

              {/* TAB 3: CONDUCT & REMARKS */}
              {activeTab === 'text' && (
                <div>
                  {docType === 'experience' && (
                    <FormTextarea
                      label="Professional Conduct Statement (Override default)"
                      value={form.conductStatement}
                      onChange={e => setForm(f => ({ ...f, conductStatement: e.target.value }))}
                      placeholder="Override or enrich the default conduct statement..."
                      presets={CONDUCT_PRESETS}
                      onSelectPreset={txt => setForm(f => ({ ...f, conductStatement: txt }))}
                      onClear={() => setForm(f => ({ ...f, conductStatement: '' }))}
                    />
                  )}

                  <FormTextarea
                    label="Additional Institutional Remarks / Recognition"
                    value={form.remarks}
                    onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                    placeholder="Institutional clearances, special recognitions, or reason for relieving..."
                    presets={REMARKS_PRESETS}
                    onSelectPreset={txt => setForm(f => ({ ...f, remarks: txt }))}
                    onClear={() => setForm(f => ({ ...f, remarks: '' }))}
                  />
                </div>
              )}

              {/* TAB 4: INSTITUTIONAL & LETTERHEAD TOGGLES */}
              {activeTab === 'letterhead' && (
                <div>
                  <FormField
                    label="Campus Address Line"
                    value={form.campusAddress}
                    onChange={e => setForm(f => ({ ...f, campusAddress: e.target.value }))}
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
                    <FormField
                      label="Campus Phone"
                      value={form.contactPhone}
                      onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))}
                    />
                    <FormField
                      label="Official Website"
                      value={form.contactWebsite}
                      onChange={e => setForm(f => ({ ...f, contactWebsite: e.target.value }))}
                    />
                  </div>

                  <FormField
                    label="Official Email"
                    value={form.contactEmail}
                    onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                    placeholder="e.g. info@therisingschool.com"
                  />

                  <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1e3a8a', marginBottom: 6, textTransform: 'uppercase' }}>
                      Show / Hide Header & Footer Elements
                    </div>
                    <FormCheckbox
                      label="Display Phone Number on Letterhead"
                      checked={form.showPhone}
                      onChange={val => setForm(f => ({ ...f, showPhone: val }))}
                    />
                    <FormCheckbox
                      label="Display Website URL on Letterhead"
                      checked={form.showWebsite}
                      onChange={val => setForm(f => ({ ...f, showWebsite: val }))}
                    />
                    <FormCheckbox
                      label="Display Email on Letterhead"
                      checked={form.showEmail}
                      onChange={val => setForm(f => ({ ...f, showEmail: val }))}
                    />
                    <FormCheckbox
                      label="Display 'The Synergy Foundation' Line"
                      checked={form.showTrust}
                      onChange={val => setForm(f => ({ ...f, showTrust: val }))}
                    />
                    <FormCheckbox
                      label="Display Security / Verification Footnote"
                      checked={form.showVerificationFootnote}
                      onChange={val => setForm(f => ({ ...f, showVerificationFootnote: val }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: LIVE REAL-TIME A4 PREVIEW */}
            <div style={{
              background: '#475569', borderRadius: 14, padding: '24px 16px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)', overflowX: 'auto'
            }}>
              <div style={{
                color: '#e2e8f0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 1.5, marginBottom: 12
              }}>
                Official A4 Letterhead Preview (Live Bidirectional Sync)
              </div>
              <div style={{
                boxShadow: '0 12px 36px rgba(0,0,0,0.45)', borderRadius: 4,
                overflow: 'hidden', background: '#ffffff'
              }}>
                {docType === 'experience'
                  ? <ExperienceLetterDoc ref={printRef} data={form} />
                  : <JoiningLetterDoc ref={printRef} data={form} />
                }
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ── HISTORY VIEW ── */}
      {view === 'history' && selTeacher && (
        <div style={panelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, borderBottom: '1.5px solid #e2e8f0', paddingBottom: 12 }}>
            <div>
              <h2 style={{ margin: 0, color: '#1e3a8a', fontSize: 18, fontWeight: 900 }}>Issued Document Archives</h2>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>Faculty: <b>{selTeacher.name}</b></div>
            </div>
            <button
              onClick={() => { setView('list'); setMsg(''); }}
              style={{ background: '#f1f5f9', color: '#1e3a8a', border: '1px solid #cbd5e1', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 700 }}
            >
              Back to List
            </button>
          </div>
          <MsgBar />
          {history.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13.5 }}>
              No documents issued yet for this teacher.
            </div>
          )}
          {history.map(doc => (
            <div key={doc._id} style={{ padding: '12px 16px', borderRadius: 10, marginBottom: 10, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 24 }}>{doc.docType === 'joining' ? '📄' : '📜'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>
                  {doc.docType === 'joining' ? 'Appointment Letter' : 'Experience & Service Certificate'}
                  {doc.status === 'revised' && <span style={{ marginLeft: 8, background: '#fef3c7', color: '#92400e', fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>REVISED</span>}
                </div>
                <div style={{ fontSize: 11.5, color: '#475569', marginTop: 2 }}>
                  Ref: <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{doc.refNumber}</span> · Issued: {fmtDate(doc.issueDate)} · Signatory: {doc.issuedBy}
                </div>
                {doc.originalRef && <div style={{ fontSize: 11, color: '#94a3b8' }}>Revises: {doc.originalRef}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => {
                    setForm({
                      ...(doc.letterData || {}),
                      refNumber: doc.refNumber,
                      issueDate: doc.issueDate,
                      docType: doc.docType,
                      teacherName: doc.teacherName,
                      issuedBy: doc.issuedBy,
                    });
                    setDocType(doc.docType);
                    setView('editor');
                  }}
                  style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 13px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                >
                  View / Edit
                </button>
                <button
                  onClick={() => handleRevise(doc)}
                  style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                >
                  Revise
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default TeacherDocuments;

